import React, { Fragment, lazy, Suspense, useEffect, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import crowdService from "services/crowdService";

import { Breadcrumbs } from "@material-ui/core";
// configs
import { PATH_NAME, PATH_HEADERS, PATH_SUB_HEADERS, USER_ROLE } from "configs";

import AccountDeletion from "features/AccountDeletion/AccountDeletion";

// types
import { IRoutes, Crumbs } from "models/IRoutes";

// layouts
import MainLayout from "layouts/MainLayout";

// containers
import AuthGuard from "guards/AuthGuard";
import GuestGuard from "guards/GuestGuard";
import TopBar from "layouts/MainLayout/TopBar";

import { userSelector } from "selectors/auth.selector";
import { setHeader, unsetHeader } from "actions/app.action";
import { PATH_MODULES } from "configs/pathName";
// route

// modules
const Error404View = lazy(() => import("features/Error404View"));
const DenyView = lazy(() => import("features/DenyView"));
const Dashboard = lazy(() => import("features/Dashboard"));
const EmployeeManagement = lazy(() => import("features/EmployeeManagement"));
const Login = lazy(() => import("features/Login"));
const EntityCreation = lazy(() => import("features/EntityCreation"));
const AddEmployee = lazy(() => import("features/AddEmployeeView"));
const LeaveManagement = lazy(() => import("features/LeaveManagement"));
const LeaveDetails = lazy(
  () => import("features/LeaveManagement/leaveDetails")
);
const DailyAttendance = lazy(() => import("features/DailyAttendance"));
const JobManagement = lazy(() => import("features/JobManagement"));
const TransportManagement = lazy(() => import("features/TransportManagement"));
const ScheduleManagement = lazy(() => import("features/SchedulerManagement"));
const JobView = lazy(() => import("features/JobView"));
const ProjectManagement = lazy(() => import("features/ProjectManagement"));
const ProjectUserManagement = lazy(
  () => import("features/ProjectManagement/MyWorkUpdates")
);
const JobEmployeeView = lazy(
  () => import("features/JobEmployeeView/LatestJobEmployeeView")
);
const AccessManagement = lazy(() => import("features/AccessManagement"));
const TaskManagement = lazy(() => import("features/TaskManagement"));
const GoalManagement = lazy(() => import("features/GoalManagement"));

const EmployeeEngagement = lazy(() => import("features/EmployeeEngagement"));
const CreateEvent = lazy(() => import("features/EmployeeEngagement/EventForm"));
const CreateBroadcast = lazy(
  () => import("features/EmployeeEngagement/BroadCast/EventForm")
);
const NoticeBoard = lazy(() => import("features/NoticeBoard"));
const CreateNotice = lazy(() => import("features/NoticeBoard/NoticeForm"));
const InventoryManagement = lazy(() => import("features/InventoryManagement"));
const DeviceDetailsView = lazy(() => import("features/InventoryDetailsView"));
const TransportSummary = lazy(() => import("features/TransportSummary"));
// const TicketsManagment = lazy(() => import("features/TicketsManagement"));
const TicketsMaster = lazy(() => import("features/TicketsMaster"));
const TicketDetailsPage = lazy(() => import("features/TicketDetailsView"));
const ProcurementTicketDetailsPage = lazy(
  () => import("features/TicketDetailsView/Procurement/Procurement")
);
const MasterData = lazy(() => import("features/MasterData"));
const UserPermissions = lazy(() => import("features/UserPermissions"));
const UserInvites = lazy(() => import("features/UserInvites"));

const UserTracking = lazy(() => import("features/UserTracking"));
const Dwyte = lazy(() => import("features/Dwyte"));
const GapAnalysis = lazy(() => import("features/GapAnalysis"));
const GapAnalysisCreateTask = lazy(
  () => import("features/GapAnalysis/CreateTaskSection")
);
const ProjectView = lazy(() => import("features/GapAnalysis/ProjectView"));
const SchedularManagementTaskDetails = lazy(
  () => import("features/SchedulerManagement/Task/TaskDetails")
);

const Signup = lazy(() => import("features/Signup"));
const AMTChat = lazy(() => import("features/AMTChat/Dashboard/Dashboard"));
const ProfileDetails = lazy(() => import("features/AMTChat/ProfilePage"));
const SignupMobileFRVerify = lazy(
  () => import("features/Signup/MobileFRVerify")
);
const CrowdSourceComponent = lazy(() => import("features/CrowdSourcePayment"));
const CrowdSource = lazy(() => import("features/CrowdSource"));

const WorkFromHome = lazy(() => import("features/WorkFromHome"));
const Rooms = lazy(() => import("features/Rooms"));
const Profiling = lazy(() => import("features/Profiling"));

const permissionBasedRoutes = [
  {
    exact: true,
    path: PATH_NAME.DASHBOARD,
    component: Dashboard,
  },

  {
    exact: true,
    path: PATH_NAME.SIGNUP,
    component: Signup,
  },

  {
    exact: true,
    path: PATH_NAME.Employees,
    header: PATH_HEADERS[PATH_NAME.Employees],
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.Employees],
      },
    ],
    component: EmployeeManagement,
    moduleaccess: PATH_MODULES[PATH_NAME.Employees],
  },
  {
    exact: true,
    path: PATH_NAME.JobManagement,
    header: PATH_HEADERS[PATH_NAME.JobManagement],
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.JobManagement],
      },
    ],
    component: JobManagement,
    moduleaccess: PATH_MODULES[PATH_NAME.JobManagement],
  },
  {
    exact: true,
    path: PATH_NAME.EmployeeEngagement,
    header: PATH_HEADERS[PATH_NAME.EmployeeEngagement],
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.EmployeeEngagement],
      },
    ],
    component: EmployeeEngagement,
    moduleaccess: PATH_MODULES[PATH_NAME.EmployeeEngagement],
  },
  {
    exact: true,
    path: PATH_NAME.CreateEvent,
    header: PATH_HEADERS[PATH_NAME.CreateEvent],
    subheader: [
      {
        label: PATH_SUB_HEADERS[PATH_NAME.EmployeeEngagement],
        path: PATH_NAME.EmployeeEngagement,
      },
      {
        path: PATH_NAME.CreateEvent,
        label: PATH_SUB_HEADERS[PATH_NAME.CreateEvent],
      },
    ],
    component: CreateEvent,
    moduleaccess: PATH_MODULES[PATH_NAME.EmployeeEngagement],
  },

  {
    exact: true,
    path: PATH_NAME.CreateBroadcast,
    header: PATH_HEADERS[PATH_NAME.CreateBroadcast],
    subheader: [
      {
        label: PATH_SUB_HEADERS[PATH_NAME.EmployeeEngagement],
        path: PATH_NAME.EmployeeEngagement,
      },
      {
        path: PATH_NAME.CreateBroadcast,
        label: PATH_SUB_HEADERS[PATH_NAME.CreateBroadcast],
      },
    ],
    component: CreateBroadcast,
    moduleaccess: PATH_MODULES[PATH_NAME.EmployeeEngagement],
  },

  {
    exact: true,
    path: PATH_NAME.EditEvent,
    header: PATH_HEADERS[PATH_NAME.EditEvent],
    subheader: [
      {
        label: PATH_SUB_HEADERS[PATH_NAME.EmployeeEngagement],
        path: PATH_NAME.EmployeeEngagement,
      },
      {
        path: PATH_NAME.EditEvent,
        label: PATH_SUB_HEADERS[PATH_NAME.EditEvent],
      },
    ],
    component: CreateEvent,
    moduleaccess: PATH_MODULES[PATH_NAME.EmployeeEngagement],
  },
  {
    exact: true,
    path: PATH_NAME.NoticeBoard,
    header: PATH_HEADERS[PATH_NAME.NoticeBoard],
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.NoticeBoard],
      },
    ],
    component: NoticeBoard,
    moduleaccess: PATH_MODULES[PATH_NAME.NoticeBoard],
  },
  {
    exact: true,
    path: PATH_NAME.CreateNotice,
    header: PATH_HEADERS[PATH_NAME.CreateNotice],
    subheader: [
      {
        label: PATH_SUB_HEADERS[PATH_NAME.NoticeBoard],
        path: PATH_NAME.NoticeBoard,
      },
      {
        path: PATH_NAME.CreateNotice,
        label: PATH_SUB_HEADERS[PATH_NAME.CreateNotice],
      },
    ],
    component: CreateNotice,
    moduleaccess: PATH_MODULES[PATH_NAME.NoticeBoard],
  },
  {
    exact: true,
    path: PATH_NAME.EditNotice,
    header: PATH_HEADERS[PATH_NAME.EditNotice],
    subheader: [
      {
        label: PATH_SUB_HEADERS[PATH_NAME.NoticeBoard],
        path: PATH_NAME.NoticeBoard,
      },
      {
        path: PATH_NAME.EditNotice,
        label: PATH_SUB_HEADERS[PATH_NAME.EditNotice],
      },
    ],
    component: CreateNotice,
    moduleaccess: PATH_MODULES[PATH_NAME.NoticeBoard],
  },
  {
    exact: true,
    path: PATH_NAME.ProjectManagement,
    header: PATH_HEADERS[PATH_NAME.ProjectManagement],
    component: ProjectManagement,
    moduleaccess: PATH_MODULES[PATH_NAME.ProjectManagement],
  },
  {
    exact: true,
    path: PATH_NAME.ProjectUserManagement,
    header: PATH_HEADERS[PATH_NAME.ProjectUserManagement],
    component: ProjectUserManagement,
    moduleaccess: PATH_MODULES[PATH_NAME.ProjectUserManagement],
  },
  {
    exact: true,
    path: PATH_NAME.DailyAttendance,
    header: PATH_HEADERS[PATH_NAME.DailyAttendance],

    component: DailyAttendance,
    moduleaccess: PATH_MODULES[PATH_NAME.DailyAttendance],
  },
  {
    exact: true,
    path: PATH_NAME.JobView,
    header: PATH_HEADERS[PATH_NAME.JobView],
    component: JobView,
    subheader: [
      {
        label: PATH_SUB_HEADERS[PATH_NAME.JobManagement],
        path: PATH_NAME.JobManagement,
      },
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.JobView],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.JobManagement],
  },
  {
    exact: true,
    path: PATH_NAME.JobEmployeeView,
    header: PATH_HEADERS[PATH_NAME.JobView],
    component: JobEmployeeView,
    subheader: [
      {
        label: PATH_SUB_HEADERS[PATH_NAME.JobManagement],
        path: PATH_NAME.JobManagement,
      },
      {
        path: PATH_NAME.JobView,
        label: PATH_SUB_HEADERS[PATH_NAME.JobView],
        triggerFunc: () => {
          window?.history?.back();
        },
      },
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.JobEmployeeView],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.JobManagement],
  },
  {
    exact: true,
    path: PATH_NAME.NewEmployee,
    header: PATH_HEADERS[PATH_NAME.NewEmployee],
    subheader: [
      {
        path: PATH_NAME.Employees,
        label: PATH_SUB_HEADERS[PATH_NAME.Employees],
      },
      {
        path: PATH_NAME.Employees,
        label: PATH_HEADERS[PATH_NAME.Employees],
      },
    ],
    component: AddEmployee,
    moduleaccess: PATH_MODULES[PATH_NAME.Employees],
  },

  {
    exact: true,
    path: PATH_NAME.LEAVE_MANAGEMENT,
    component: LeaveManagement,
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.LEAVE_MANAGEMENT],
      },
    ],
    header: PATH_HEADERS[PATH_NAME.LEAVE_MANAGEMENT],
    moduleaccess: PATH_MODULES[PATH_NAME.LEAVE_MANAGEMENT],
  },

  {
    exact: true,
    path: PATH_NAME.LeaveDetails,
    component: LeaveDetails,
    header: PATH_HEADERS[PATH_NAME.LeaveDetails],
    moduleaccess: PATH_MODULES[PATH_NAME.LeaveDetails],
    subheader: [
      {
        path: PATH_NAME.LEAVE_MANAGEMENT,
        label: PATH_SUB_HEADERS[PATH_NAME.LEAVE_MANAGEMENT],
      },
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.LeaveDetails],
      },
    ],
  },

  {
    exact: true,
    path: PATH_NAME.MasterData,
    component: MasterData,
    header: PATH_HEADERS[PATH_NAME.MasterData],
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.MasterData],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.MasterData],
  },
  {
    exact: true,
    path: PATH_NAME.TransportManagement,
    component: TransportManagement,
    header: PATH_HEADERS[PATH_NAME.TransportManagement],
    moduleaccess: PATH_MODULES[PATH_NAME.TransportManagement],
  },
  {
    exact: true,
    path: PATH_NAME.TransportSummary,
    component: TransportSummary,
    header: PATH_HEADERS[PATH_NAME.TransportSummary],
    subheader: [
      {
        path: PATH_NAME.TransportManagement,
        label: PATH_SUB_HEADERS[PATH_NAME.TransportManagement],
      },
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.TransportSummary],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.TransportManagement],
  },
  {
    exact: true,
    path: PATH_NAME.ScheduleManagement,
    header: PATH_HEADERS[PATH_NAME.ScheduleManagement],
    subheader: [
      {
        path: PATH_NAME.ScheduleManagement,
        label: PATH_SUB_HEADERS[PATH_NAME.ScheduleManagement],
      },
    ],
    component: ScheduleManagement,
    moduleaccess: PATH_MODULES[PATH_NAME.ScheduleManagement],
  },

  {
    exact: true,
    path: PATH_NAME.ScheduleManagementTaskDetails,
    header: PATH_HEADERS[PATH_NAME.ScheduleManagement],
    subheader: [
      {
        path: PATH_NAME.ScheduleManagement,
        label: PATH_SUB_HEADERS[PATH_NAME.ScheduleManagement],
      },
      {
        path: PATH_NAME.ScheduleManagement,
        label: PATH_SUB_HEADERS[PATH_NAME.ScheduleManagementTasks],
      },
      {
        path: PATH_NAME.ScheduleManagement,
        label: PATH_SUB_HEADERS[PATH_NAME.ScheduleManagementTaskDetails],
      },
    ],
    component: SchedularManagementTaskDetails,
    moduleaccess: PATH_MODULES[PATH_NAME.ScheduleManagement],
  },

  {
    exact: true,
    path: PATH_NAME.EditEmployee,
    header: PATH_HEADERS[PATH_NAME.EditEmployee],
    subheader: [
      {
        path: PATH_NAME.Employees,
        label: PATH_SUB_HEADERS[PATH_NAME.Employees],
      },
      {
        path: PATH_NAME.Employees,
        label: PATH_HEADERS[PATH_NAME.EditEmployee],
      },
    ],
    component: AddEmployee,
    moduleaccess: PATH_MODULES[PATH_NAME.Employees],
  },
  {
    exact: true,
    path: PATH_NAME.InventoryManagement,
    header: PATH_HEADERS[PATH_NAME.InventoryManagement],
    subheader: [
      {
        path: PATH_NAME.InventoryManagement,
        label: PATH_SUB_HEADERS[PATH_NAME.InventoryManagement],
      },
    ],
    component: InventoryManagement,
    moduleaccess: PATH_MODULES[PATH_NAME.InventoryManagement],
  },
  {
    exact: true,
    path: PATH_NAME.InventoryView,
    header: PATH_HEADERS[PATH_NAME.InventoryView],
    component: DeviceDetailsView,
    subheader: [
      {
        label: PATH_SUB_HEADERS[PATH_NAME.InventoryManagement],
        path: PATH_NAME.InventoryManagement,
      },
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.InventoryView],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.InventoryManagement],
  },
  {
    exact: true,
    path: PATH_NAME.Tickets,
    header: PATH_HEADERS[PATH_NAME.Tickets],
    // component: TicketsManagment,
    component: TicketsMaster,
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.Tickets],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.Tickets],
  },
  {
    exact: true,
    path: PATH_NAME.TicketDetails,
    header: PATH_HEADERS[PATH_NAME.TicketDetails],
    component: TicketDetailsPage,
    subheader: [
      {
        path: PATH_NAME.Tickets,
        label: PATH_SUB_HEADERS[PATH_NAME.Tickets],
      },
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.TicketDetails],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.Tickets],
  },
  {
    exact: true,
    path: PATH_NAME.ProcurementTicketDetails,
    header: PATH_HEADERS[PATH_NAME.ProcurementTicketDetails],
    component: ProcurementTicketDetailsPage,
    subheader: [
      {
        path: PATH_NAME.Tickets,
        label: PATH_SUB_HEADERS[PATH_NAME.Tickets],
      },
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.ProcurementTicketDetails],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.Tickets],
  },
  {
    exact: true,
    path: PATH_NAME.UserPermissions,
    header: PATH_HEADERS[PATH_NAME.UserPermissions],
    component: UserPermissions,
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.UserPermissions],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.UserPermissions],
  },
  {
    exact: true,
    path: PATH_NAME.AccessManagement,
    header: PATH_HEADERS[PATH_NAME.AccessManagement],
    component: AccessManagement,
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.AccessManagement],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.UserPermissions],
  },
  {
    exact: true,
    path: PATH_NAME.Invites,
    header: PATH_HEADERS[PATH_NAME.Invites],
    component: UserInvites,
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.Invites],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.Invites],
  },
  // {
  //   exact: true,
  //   path: PATH_NAME.TaskManagement,
  //   header: PATH_HEADERS[PATH_NAME.TaskManagement],
  //   component: TaskManagement,
  //   subheader: [
  //     {
  //       path: "",
  //       label: PATH_SUB_HEADERS[PATH_NAME.TaskManagement],
  //     },
  //   ],
  //   moduleaccess: PATH_MODULES[PATH_NAME.TaskManagement],
  // },
  {
    exact: true,
    path: PATH_NAME.GoalManagement,
    header: PATH_HEADERS[PATH_NAME.GoalManagement],
    component: GoalManagement,
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.GoalManagement],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.GoalManagement],
  },
  {
    exact: true,
    path: PATH_NAME.UserTracking,
    header: PATH_HEADERS[PATH_NAME.UserTracking],
    component: UserTracking,
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.UserTracking],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.UserTracking],
  },
  {
    exact: true,
    path: PATH_NAME.GapAnalysis,
    header: PATH_HEADERS[PATH_NAME.GapAnalysis],
    component: GapAnalysis,
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.GapAnalysis],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.GapAnalysis],
  },
  {
    exact: true,
    path: PATH_NAME.GapAnalysisCreateTask,
    header: PATH_HEADERS[PATH_NAME.GapAnalysisCreateTask],
    subheader: [
      {
        label: PATH_SUB_HEADERS[PATH_NAME.GapAnalysis],
        path: PATH_NAME.GapAnalysis,
      },
      {
        path: PATH_NAME.GapAnalysisCreateTask,
        label: PATH_SUB_HEADERS[PATH_NAME.GapAnalysisCreateTask],
      },
    ],
    component: GapAnalysisCreateTask,
    moduleaccess: PATH_MODULES[PATH_NAME.GapAnalysis],
  },
  {
    exact: true,
    path: PATH_NAME.GapAnalysisViewProject,
    header: PATH_HEADERS[PATH_NAME.GapAnalysisViewProject],
    subheader: [
      {
        label: PATH_SUB_HEADERS[PATH_NAME.GapAnalysis],
        path: PATH_NAME.GapAnalysis,
      },
      {
        path: PATH_NAME.GapAnalysisViewProject,
        label: PATH_SUB_HEADERS[PATH_NAME.GapAnalysisViewProject],
      },
    ],
    component: ProjectView,
    moduleaccess: PATH_MODULES[PATH_NAME.GapAnalysis],
  },
  {
    exact: true,
    path: PATH_NAME.CrowdsourcePayment,
    header: PATH_HEADERS[PATH_NAME.CrowdsourcePayment],
    subheader: [
      {
        path: PATH_NAME.CrowdsourcePayment,
        label: PATH_SUB_HEADERS[PATH_NAME.CrowdsourcePayment],
      },
    ],
    component: CrowdSourceComponent,
    moduleaccess: PATH_MODULES[PATH_NAME.CrowdsourcePayment],
  },

  {
    exact: true,
    path: PATH_NAME.Crowdsource,
    header: PATH_HEADERS[PATH_NAME.Crowdsource],
    component: CrowdSource,

    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.Crowdsource],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.Crowdsource],
  },

  {
    exact: true,
    path: PATH_NAME.WorkFromHome,
    header: PATH_HEADERS[PATH_NAME.WorkFromHome],
    component: WorkFromHome,

    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.WorkFromHome],
      },
    ],
    moduleaccess: PATH_MODULES[PATH_NAME.WorkFromHome],
  },

  {
    exact: true,
    path: PATH_NAME.AcxioRooms,
    header: PATH_HEADERS[PATH_NAME.AcxioRooms],
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.AcxioRooms],
      },
    ],
    component: Rooms,
    moduleaccess: PATH_MODULES[PATH_NAME.AcxioRooms],
  },

  {
    exact: true,
    path: PATH_NAME.Profiling,
    header: PATH_HEADERS[PATH_NAME.Profiling],
    subheader: [
      {
        path: "",
        label: PATH_SUB_HEADERS[PATH_NAME.Profiling],
      },
    ],
    component: Profiling,
    moduleaccess: PATH_MODULES[PATH_NAME.Profiling],
  },

  {
    component: () => <Redirect to={PATH_NAME.ERROR_404} />,
  },
];

const routesConfig: IRoutes[] = [
  // {
  //   exact: true,
  //   path: "/",
  //   component: () => <Redirect to={PATH_NAME.Employees} />,
  // },
  {
    exact: true,
    path: PATH_NAME.ERROR_404,
    component: Error404View,
  },
  {
    exact: true,
    path: PATH_NAME.LOGIN,
    component: Login,
  },

  {
    exact: true,
    path: PATH_NAME.Dwyte,
    component: Dwyte,
  },

  {
    exact: true,
    path: PATH_NAME.LOGIN,
    component: AccountDeletion,
  },
  {
    exact: true,
    path: PATH_NAME.ERROR_403,
    component: DenyView,
  },
  {
    exact: true,
    path: PATH_NAME.EntityCreation,
    component: EntityCreation,
  },
  {
    exact: true,
    path: PATH_NAME.DeleteAccount,
    component: AccountDeletion,
  },

  // {
  //   path: "/admin",
  //   guard: AuthGuard,
  //   layout: MainLayout,
  //   routes: permissionBasedRoutes,
  // },

  {
    exact: true,
    path: PATH_NAME.AmtChatService,
    component: AMTChat,
  },

  {
    exact: true,
    path: PATH_NAME.AmtChatProfileDetails,
    component: ProfileDetails,
  },

  {
    exact: true,
    path: PATH_NAME.SIGNUP,
    component: Signup,
  },

  {
    exact: true,
    path: PATH_NAME.SignupMobileFrRegistration,
    component: SignupMobileFRVerify,
  },
];

function ComponentWrapper(props: any) {
  const { children, header, subheader } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const isChatPath = location.pathname.includes("/amt-chat-service");
  const fontClass = isChatPath ? "font-manrope" : "font-lexend";
  useEffect(() => {
    dispatch(setHeader(header, subheader));
  }, [header, subheader]);

  return (
    <div id="wrapper" style={{ height: "100%" }} className={fontClass}>
      {children}
    </div>
  );
}

function renderRoutes(routes: IRoutes[]) {
  return (
    <>
      {routes ? (
        // @ts-ignore
        <Suspense fallback={<div />}>
          <Switch>
            {routes.map((route: IRoutes, idx: number) => {
              const Guard = route.guard || Fragment;
              const Layout = route.layout || Fragment;
              const Component = route.component;
              const token = localStorage.getItem("auth_token");
              return (
                <Route
                  key={`routes-${idx}`}
                  path={route.path}
                  exact={route.exact}
                  render={(props: any) => {
                    console.log(route.routes);

                    return (
                      <Guard header={route.header} subheader={route.subheader}>
                        {/* @ts-ignore */}
                        <Layout
                          header={route.header}
                          subheader={route.subheader}
                        >
                          {route.routes ? (
                            renderRoutes(route.routes)
                          ) : (
                            <ComponentWrapper
                              {...props}
                              header={route.header}
                              subheader={route.subheader}
                            >
                              <Component />
                            </ComponentWrapper>
                          )}
                        </Layout>
                      </Guard>
                    );
                  }}
                />
              );
            })}
            {/* <Redirect to={PATH_NAME.LOGIN} /> */}
          </Switch>
        </Suspense>
      ) : null}
    </>
  );
}

function Routes() {
  let history = useHistory();
  const userinfo = useSelector(userSelector);
  const [curRoutes, setCurRoutes] = useState<IRoutes[]>([
    ...routesConfig,
    {
      path: "*",
      guard: AuthGuard,
      component: () => <div>Loading....</div>,
    },
  ]);
  const [paymentAccess, setPaymentAccess] = useState<any>(null);

  useEffect(() => {
    if (userinfo?.currentuserinfo?.module_accesses) {
      let tempRoutes = permissionBasedRoutes.filter((route) => {
        return userinfo.currentuserinfo.module_accesses?.includes(
          route.moduleaccess
        );
      });

      if (
        userinfo.currentuserinfo.module_accesses.includes(19) &&
        userinfo.currentuserinfo.module_accesses.includes(21)
      ) {
        tempRoutes = tempRoutes.filter((route) => route.moduleaccess !== 21);
      }

      // NOTE: giving access to payments, only if has write or read access
      if (
        !(
          paymentAccess &&
          ["write", "read"].includes(paymentAccess?.access?.toLowerCase())
        )
      ) {
        tempRoutes = tempRoutes.filter((route) => route.moduleaccess !== 21);
      }

      let tempRoute: any;

      if (
        userinfo.currentuserinfo.module_accesses.includes(1) ||
        userinfo.currentuserinfo.module_accesses.includes(8)
      ) {
        tempRoute = PATH_NAME.Employees;
        setCurRoutes([
          ...routesConfig,
          {
            path: "/admin",
            guard: AuthGuard,
            layout: MainLayout,
            routes: tempRoutes,
          },
          {
            exact: true,
            path: "/",
            component: () => (
              <Redirect to={tempRoute ? tempRoute : PATH_NAME.ERROR_404} />
            ),
          },
        ]);
      } else {
        const tempCurRoute = userinfo.currentuserinfo.module_accesses[0];
        const findRouteByValue = (value: number): string | undefined => {
          for (const route in PATH_MODULES) {
            if (
              PATH_MODULES.hasOwnProperty(route) &&
              PATH_MODULES[route] === value
            ) {
              return route;
            }
          }
          return undefined;
        };

        const tempRoute: any = findRouteByValue(tempCurRoute);
        setCurRoutes([
          ...routesConfig,
          {
            path: "/admin",
            guard: AuthGuard,
            layout: MainLayout,
            routes: tempRoutes,
          },
          {
            exact: true,
            path: "/",
            component: () => (
              <Redirect to={tempRoute ? tempRoute : PATH_NAME.ERROR_404} />
            ),
          },
        ]);
      }
    }
  }, [userinfo, paymentAccess]);

  useEffect(() => {
    if (userinfo?.currentuserinfo?.module_accesses) {
      crowdService
        .getPaymentAccess()
        .then((res) => {
          setPaymentAccess(res?.data);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [userinfo]);

  useEffect(() => {
    if (
      userinfo?.currentuserinfo?.access_level === "user" &&
      userinfo?.isuserlogin === true
    ) {
      console.log(userinfo, "user info in the section");

      history.push(PATH_NAME.AmtChatService);

      // return <Redirect to={`${PATH_NAME.AmtChatService}`} />;
    }
  }, [userinfo]);

  useEffect(() => {
    const unlisten = history?.listen((location, action) => {
      window.scrollTo(0, 0);
      console.log("Route changed:", location.pathname);
    });
    return () => {
      unlisten();
    };
  }, [history]);

  return <>{renderRoutes(curRoutes)}</>;
}

export default Routes;
